import React from "react";

/**
 * ErrorBoundary — catches render errors in children and shows a recovery UI
 * instead of a white screen. Wrap around any component tree that might crash.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, showDetails: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error("[ErrorBoundary] Caught error:", error, errorInfo);
  }

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoToDashboard = () => {
    // Clear any stale state and navigate to root
    localStorage.removeItem("interviewConfig");
    window.location.href = "/";
  };

  toggleDetails = () => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }));
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.icon}>⚠️</div>
            <h1 style={styles.title}>Something went wrong</h1>
            <p style={styles.message}>
              An unexpected error occurred. You can try refreshing the page or
              returning to the dashboard.
            </p>

            <div style={styles.actions}>
              <button onClick={this.handleRefresh} style={styles.primaryBtn}>
                🔄 Refresh Page
              </button>
              <button onClick={this.handleGoToDashboard} style={styles.secondaryBtn}>
                🏠 Go to Dashboard
              </button>
            </div>

            <button onClick={this.toggleDetails} style={styles.detailsToggle}>
              {this.state.showDetails ? "▲ Hide" : "▼ Show"} error details
            </button>

            {this.state.showDetails && (
              <pre style={styles.details}>
                {this.state.error?.toString()}
                {"\n\n"}
                {this.state.errorInfo?.componentStack}
              </pre>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const styles = {
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "60vh",
    padding: "2rem",
    fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
  },
  card: {
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
    padding: "2.5rem",
    maxWidth: "480px",
    width: "100%",
    textAlign: "center",
  },
  icon: {
    fontSize: "3rem",
    marginBottom: "0.5rem",
  },
  title: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: "#1a1a2e",
    margin: "0.5rem 0",
  },
  message: {
    color: "#555",
    fontSize: "0.95rem",
    lineHeight: 1.5,
    marginBottom: "1.5rem",
  },
  actions: {
    display: "flex",
    gap: "0.75rem",
    justifyContent: "center",
    flexWrap: "wrap",
    marginBottom: "1rem",
  },
  primaryBtn: {
    padding: "0.65rem 1.5rem",
    fontSize: "0.95rem",
    fontWeight: 600,
    color: "#fff",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
  },
  secondaryBtn: {
    padding: "0.65rem 1.5rem",
    fontSize: "0.95rem",
    fontWeight: 600,
    color: "#667eea",
    background: "#f0f0ff",
    border: "1px solid #667eea",
    borderRadius: "8px",
    cursor: "pointer",
  },
  detailsToggle: {
    background: "none",
    border: "none",
    color: "#888",
    fontSize: "0.8rem",
    cursor: "pointer",
    marginTop: "0.5rem",
  },
  details: {
    marginTop: "0.75rem",
    padding: "1rem",
    background: "#f8f8f8",
    borderRadius: "8px",
    textAlign: "left",
    fontSize: "0.75rem",
    color: "#c0392b",
    maxHeight: "200px",
    overflow: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
};

export default ErrorBoundary;
